import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BellIcon,
  BotIcon,
  LogOutIcon,
  MoonIcon,
  ShieldCheckIcon,
  SunIcon,
  WalletIcon,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

import { useTheme as useAppTheme } from "../../hooks/use-theme";
import { hasPermission, selectUser } from "../../pages/login/selectors";
import { Permissions } from "../../entities/permissions";
import { Features } from "../../entities/features";
import { useIsFeatureEnabled } from "../../services/api/featuresApi";
import { hasNotifications } from "../../entities/notification-type";
import { clearSession } from "../../session/actions";
import { useLazyMeQuery } from "../../services/api";

import LoggedCard from "../../components/logged-card";
import NavSection from "../../components/NavSection";
import WorkspaceSelectorDropdown from "../../components/workspace-selector-dropdown";
import ManageUserNotificationsDialog from "../../components/users/manage-user-notifications-dialog";
import EditUserDialog from "../../components/users/edit-user-dialog";

import { useConsent } from "../../analytics/ConsentContext";
import { clearStorageKeepingConsent } from "../../analytics/consent";

import NavConfig from "./NavConfig";

const DRAWER_WIDTH = 280;

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { t } = useTranslation();
  const { theme: themeMode, toggleTheme } = useAppTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [openNotificationsManager, setOpenNotificationsManager] =
    useState(false);
  const [openEditProfile, setOpenEditProfile] = useState(false);

  const user = useSelector(selectUser());
  const canManageIntegrations = useSelector(hasPermission(Permissions.PAGE_INTEGRATIONS_ROOT));
  const agentAccessEnabled = useIsFeatureEnabled(Features.FT_MCP);
  const [triggerMe] = useLazyMeQuery();
  const { openPrompt: openConsentPrompt } = useConsent();

  // Close the mobile drawer on route changes so the new page isn't
  // hidden behind it. Desktop is `variant=persistent`, so this is a
  // no-op there.
  useEffect(() => {
    if (isOpenSidebar) onCloseSidebar?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <div className="flex h-full flex-col overflow-y-auto">
      <WorkspaceSelectorDropdown />
      <div className="mx-4 mb-2 border-t border-border" />

      <NavSection navConfig={NavConfig()} />

      <div className="flex-1" />

      <div className="mx-5 mb-10">
        <ManageUserNotificationsDialog
          user={user}
          open={openNotificationsManager}
          onClose={() => setOpenNotificationsManager(false)}
          onConfirm={() => {
            setOpenNotificationsManager(false);
            triggerMe(undefined);
          }}
        />
        <EditUserDialog
          title={t("sidebar.edit-profile")}
          value={user}
          open={openEditProfile}
          onClose={() => setOpenEditProfile(false)}
          onEdited={() => {
            setOpenEditProfile(false);
            triggerMe(undefined);
          }}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center rounded-md px-3 py-2 text-left transition-colors hover:bg-accent"
            >
              <LoggedCard user={user} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="start"
            sideOffset={8}
            className="w-64"
          >
            <div className="rounded-md bg-muted/50 p-3">
              <LoggedCard user={user} onEdit={() => setOpenEditProfile(true)} />
            </div>
            <DropdownMenuSeparator />
            {user && hasNotifications(user.role) ? (
              <DropdownMenuItem
                onSelect={() => setOpenNotificationsManager(true)}
              >
                <BellIcon className="size-4" />
                {t("sidebar.manage-notifications")}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              onSelect={() => navigate("/dashboard/credits")}
            >
              <WalletIcon className="size-4" />
              {t("sidebar.credits")}
            </DropdownMenuItem>
            {canManageIntegrations && agentAccessEnabled ? (
              <DropdownMenuItem
                onSelect={() => navigate("/dashboard/integrations")}
              >
                <BotIcon className="size-4" />
                {t("sidebar.integrations")}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              onSelect={(e) => {
                // Keep the menu open — toggling a mode in place feels right
                // and matches the previous popover behaviour.
                e.preventDefault();
                toggleTheme();
              }}
            >
              {themeMode === "dark" ? (
                <SunIcon className="size-4" />
              ) : (
                <MoonIcon className="size-4" />
              )}
              {t(
                `sidebar.${themeMode === "dark" ? "light-mode" : "dark-mode"}`,
                {
                  defaultValue:
                    themeMode === "dark" ? "Light mode" : "Dark mode",
                },
              )}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                openConsentPrompt();
              }}
            >
              <ShieldCheckIcon className="size-4" />
              {t("sidebar.privacy-preferences")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              onSelect={() => {
                dispatch(clearSession());
                clearStorageKeepingConsent();
              }}
            >
              <LogOutIcon className="size-4" />
              {t("sidebar.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <div
      className={cn("lg:shrink-0")}
      style={{ width: undefined }}
    >
      {/* Mobile drawer */}
      <Sheet
        open={isOpenSidebar}
        onOpenChange={(next) => {
          if (!next) onCloseSidebar?.();
        }}
      >
        <SheetContent
          side="left"
          className="w-[280px] p-0 lg:hidden"
          showCloseButton={false}
        >
          <SheetTitle className="sr-only">{t("sidebar.navigation", "Navigation")}</SheetTitle>
          {renderContent}
        </SheetContent>
      </Sheet>

      {/* Desktop persistent aside */}
      <aside
        className="fixed inset-y-0 left-0 hidden border-r border-dashed border-border bg-muted/30 lg:block"
        style={{ width: DRAWER_WIDTH }}
      >
        {renderContent}
      </aside>

      {/* Spacer so page content offsets behind the persistent aside */}
      <div
        className="hidden lg:block"
        style={{ width: DRAWER_WIDTH }}
        aria-hidden
      />
    </div>
  );
}
